import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { ChevronDown, HelpCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const FAQ = () => {
  const [openItem, setOpenItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const isIOSApp = useIsIOSApp();

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      faqs: [
        {
          q: 'What is ExpatsSphere?',
          a: 'ExpatsSphere is a global community platform designed specifically for expats, digital nomads, and international travelers. We help you discover events, connect with like-minded people, and build lasting friendships in your new city.'
        },
        {
          q: 'How do I create an account?',
          a: 'Click the "Sign Up" button in the navigation bar, fill in your details including your name, email, nationality, and current location. You can sign up for free and explore the platform immediately.'
        },
        {
          q: 'Is ExpatsSphere available in my city?',
          a: 'ExpatsSphere is a global platform with members in over 100 cities worldwide. Even if there aren\'t many members in your city yet, you can help grow the community by creating events and inviting other expats!'
        },
        {
          q: 'Do I need to pay to use ExpatsSphere?',
          a: 'Great news! Membership is completely FREE for 1 year from when you sign up. Enjoy all premium features including creating events, direct messaging, and more - no credit card required!'
        }
      ]
    },
    {
      id: 'membership',
      title: 'Membership & Pricing',
      faqs: [
        {
          q: 'What does membership include?',
          a: 'Premium membership includes: unlimited event creation, direct messaging with any member, priority RSVPs for popular events, a "Who Viewed My Profile" feature, access to exclusive member-only events, and a verified member badge.'
        },
        {
          q: 'How much does membership cost?',
          a: 'Membership is FREE for 1 year from when you join! No credit card required. After your first year, membership will be $12/year (that\'s only $1/month). Join now and start connecting!'
        },
        {
          q: 'What payment methods do you accept?',
          a: 'We accept all major credit cards (Visa, MasterCard, American Express) through our secure payment processor, Stripe.'
        },
        {
          q: 'Can I get a refund?',
          a: 'Due to the digital nature of our service, memberships are generally non-refundable. However, if you experience issues within the first 7 days, please contact our support team and we\'ll work to find a solution.'
        },
        {
          q: 'Does my membership auto-renew?',
          a: 'No, ExpatsSphere memberships do not auto-renew. You\'ll receive a reminder before your membership expires, and you can choose to renew manually.'
        }
      ]
    },
    {
      id: 'events',
      title: 'Events & Activities',
      faqs: [
        {
          q: 'How do I create an event?',
          a: 'Click "Create Your Event" in the navigation bar, fill in the event details (title, description, date, location, category), set your attendance limit, and publish. Premium members can create unlimited events.'
        },
        {
          q: 'What types of events can I create?',
          a: 'You can create any type of social event: coffee meetups, hiking trips, language exchanges, cultural outings, networking events, dinner parties, sports activities, and more. Events should follow our community guidelines and be appropriate for all members.'
        },
        {
          q: 'How do I RSVP to an event?',
          a: 'Navigate to the event page and click the "Attend" button. You\'ll receive a confirmation email and the event will appear in your calendar. Premium members get priority access to popular events.'
        },
        {
          q: 'Can I cancel my RSVP?',
          a: 'Yes, you can cancel your RSVP at any time by going to the event page and clicking "Cancel Attendance." We encourage giving at least 24 hours notice so others can take your spot.'
        },
        {
          q: 'What happens if I don\'t show up to an event?',
          a: 'We ask that you cancel if you can\'t attend. Repeated no-shows may affect your ability to RSVP to future events, as it impacts the experience for other members and event organizers.'
        }
      ]
    },
    {
      id: 'connections',
      title: 'Connections & Messaging',
      faqs: [
        {
          q: 'How do I connect with other members?',
          a: 'Visit a member\'s profile and click "Connect." They\'ll receive a connection request. Once accepted, you can message each other directly. You can also connect with people you meet at events.'
        },
        {
          q: 'Can anyone message me?',
          a: 'By default, only your connections can message you. Premium members can message any member. You can adjust your messaging preferences in Privacy Settings.'
        },
        {
          q: 'How do I find expats from my country?',
          a: 'Use the "Find Expats" page and filter by nationality. You can also search by location, interests, or browse members attending events in your city.'
        },
        {
          q: 'Can I block someone?',
          a: 'Yes. Go to their profile and select "Block User" from the menu. Blocked users cannot see your profile, send you messages, or see your activity.'
        }
      ]
    },
    {
      id: 'privacy',
      title: 'Privacy & Safety',
      faqs: [
        {
          q: 'Who can see my profile?',
          a: 'By default, your profile is visible to all logged-in ExpatsSphere members. You can adjust your privacy settings to limit who can see certain information or make your profile visible only to connections.'
        },
        {
          q: 'How is my data protected?',
          a: 'We use industry-standard encryption to protect your data. We never sell your personal information to third parties. For full details, please review our Privacy Policy.'
        },
        {
          q: 'How do you keep the community safe?',
          a: 'We use a combination of AI-powered content moderation and human review to ensure our community stays safe and respectful. Members can report inappropriate content or behavior, and our team reviews all reports within 24 hours.'
        },
        {
          q: 'What should I do if I experience harassment?',
          a: 'Use the "Report" button on the user\'s profile or the specific content. You can also block the user immediately. Our safety team takes all reports seriously and will investigate promptly.'
        },
        {
          q: 'Are events safe to attend?',
          a: 'While we moderate event listings, attendees should always use common sense: meet in public places, tell someone where you\'re going, and trust your instincts. Report any concerns to our support team.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account & Technical',
      faqs: [
        {
          q: 'How do I edit my profile?',
          a: 'Click on your profile picture in the navigation bar, then select "Profile" or "Edit Profile." You can update your photo, bio, interests, location, and other details.'
        },
        {
          q: 'How do I change my email or password?',
          a: 'Go to Settings > Account to update your email address or password. You\'ll need to confirm your current password to make changes.'
        },
        {
          q: 'How do I manage email notifications?',
          a: 'Visit Settings > Privacy Settings > Email Notifications. You can customize which notifications you receive, including event reminders, connection requests, and community updates.'
        },
        {
          q: 'How do I delete my account?',
          a: 'Go to Settings > Account > Delete Account. This will permanently remove your profile, events, messages, and all associated data. This action cannot be undone.'
        },
        {
          q: 'The website isn\'t working properly. What should I do?',
          a: 'Try refreshing the page, clearing your browser cache, or using a different browser. If issues persist, contact our support team at Support@expatssphere.com with details about the problem.'
        }
      ]
    }
  ];

  // On iOS PWA, filter out payment/membership content for Apple compliance
  const baseCategories = isIOSApp
    ? faqCategories
        .filter(cat => cat.id !== 'membership')
        .map(cat => ({
          ...cat,
          faqs: cat.faqs
            .filter(faq => !faq.q.toLowerCase().includes('pay to use'))
            .map(faq => ({
              ...faq,
              a: faq.a.replace(/Premium members/g, 'Members').replace(/premium membership/gi, 'membership')
            }))
        }))
    : faqCategories;

  const filteredCategories = searchQuery
    ? baseCategories.map(cat => ({
        ...cat,
        faqs: cat.faqs.filter(
          faq =>
            faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
            faq.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.faqs.length > 0)
    : baseCategories;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
                <HelpCircle className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Everything you need to know about ExpatsSphere. Can't find what you're looking for? 
                Contact our support team.
              </p>

              {/* Search */}
              <div className="max-w-xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search FAQs..."
                  className="h-14 pl-12 pr-4 rounded-full border-2 text-lg"
                />
              </div>
            </div>

            {/* FAQ Categories */}
            <div className="space-y-8">
              {filteredCategories.map((category) => (
                <div key={category.id} id={category.id}>
                  <h2 className="text-2xl font-heading font-bold mb-4 text-primary">
                    {category.title}
                  </h2>
                  <div className="bg-white rounded-3xl border-2 border-border overflow-hidden">
                    {category.faqs.map((faq, index) => (
                      <div key={index} className="border-b border-border last:border-b-0">
                        <button
                          onClick={() =>
                            setOpenItem(
                              openItem === `${category.id}-${index}` 
                                ? null 
                                : `${category.id}-${index}`
                            )
                          }
                          className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
                        >
                          <span className="font-medium pr-4">{faq.q}</span>
                          <ChevronDown
                            className={`w-5 h-5 text-muted-foreground transition-transform flex-shrink-0 ${
                              openItem === `${category.id}-${index}` ? 'rotate-180' : ''
                            }`}
                          />
                        </button>
                        <AnimatePresence>
                          {openItem === `${category.id}-${index}` && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                              className="overflow-hidden"
                            >
                              <div className="px-6 pb-5 text-muted-foreground">
                                {faq.a}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Still have questions */}
            <div className="mt-12 bg-gradient-to-r from-primary to-accent rounded-3xl p-8 text-center text-white">
              <h2 className="text-2xl font-heading font-bold mb-4">Still Have Questions?</h2>
              <p className="text-white/90 mb-6 max-w-lg mx-auto">
                Our support team is always happy to help. Reach out and we'll get back to you within 24 hours.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center justify-center h-12 px-8 bg-white text-primary font-bold rounded-full hover:bg-white/90 transition-colors"
              >
                Contact Support
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FAQ;
